import { Cross2Icon } from '@radix-ui/react-icons';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProfileId } from '@/features/org/hooks/use-profile-id';
import {
  useAddSubprojectComment,
  useDeleteSubprojectComment,
  useSubprojectComments,
  useUpdateSubprojectCommentSeen,
} from '@/features/projects/create-subproject/hooks/use-subproject-comments';
import ArrowDown from '@/shared/assets/arrow-down.png';
import { CommentInput } from '@/shared/components/comments/comment-input';
import { CommentBase } from '@/shared/components/comments/comment-type';
import { CommentsList } from '@/shared/components/comments/comments-list';
import { Gradients } from '@/shared/components/theme/gradient';
import { Button } from '@/shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { ProjectType } from '@/shared/types/api';
import { cn } from '@/shared/utils/cn';

export const ProjectComments = ({
  clientViewId,
  contractorViewId,
  type,
  internalSubproject,
  className,
}: {
  clientViewId: number;
  contractorViewId: number;
  type: ProjectType;
  internalSubproject: boolean;
  className?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation('project', { keyPrefix: 'Comments' });

  const {
    data: comments,
    isLoading,
    isError,
  } = useSubprojectComments({
    clientViewId,
    contractorViewId,
    type,
  });
  const addComment = useAddSubprojectComment({
    clientViewId,
    contractorViewId,
    type,
  });
  const deleteComment = useDeleteSubprojectComment({
    clientViewId,
    contractorViewId,
    type,
  });
  const updateSeen = useUpdateSubprojectCommentSeen({
    clientViewId,
    contractorViewId,
    type,
  });

  const handleSubmit = (message: string) => {
    if (!message.trim()) return;
    addComment.mutate(message.trim());
  };

  const { data: profileId } = useProfileId();
  const unseenCommentsCount = useMemo(() => {
    return (
      comments?.filter(
        (comment) => !comment.seen && comment.author_profile_id !== profileId,
      ).length ?? 0
    );
  }, [comments, profileId]);

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          btnType="secondary"
          className={cn('relative flex items-center gap-2', className)}
        >
          <img
            src={ArrowDown}
            alt="arrow-down"
            className={cn(
              'size-4 transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
          />
          <span>{t('trigger')}</span>
          {unseenCommentsCount > 0 && (
            <span
              className={cn(
                'absolute -right-2 -top-2 flex h-5 min-h-5 min-w-5 items-center justify-center rounded-full px-1 text-xs text-black',
                Gradients.greenGradient,
              )}
            >
              {unseenCommentsCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        className="w-[396px] border-none bg-transparent p-0"
      >
        <div
          className="relative rounded-[16px] border-[0.5px] border-transparent backdrop-blur-md"
          style={{
            background:
              'linear-gradient(rgba(255,255,255,0.2), rgba(255,255,255,0.2)), linear-gradient(113.245deg, rgba(68, 0, 155, 0.2) 0%, rgba(35, 112, 202, 0.2) 100%)',
            backgroundOrigin: 'border-box',
            backgroundClip: 'padding-box, border-box',
          }}
        >
          <div className="flex flex-col gap-[16px] rounded-[16px] bg-transparent p-[8px]">
            {/* Title Container */}
            <div className="flex items-center justify-between px-[8px] pb-0 pl-[8px] pr-[6px] pt-[2px]">
              <p className="text-[13px] font-normal leading-[18px] tracking-[-0.4px] text-[#fefefe]">
                {t('title')}
              </p>
              <button
                onClick={() => setIsOpen(false)}
                className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
              >
                <Cross2Icon className="size-4 text-white" />
                <span className="sr-only">Close</span>
              </button>
            </div>

            {/* Divider */}
            <div className="h-[0.5px] w-full bg-white/50" />

            {/* Content */}
            <div className="flex flex-col gap-[16px]">
              <div className="flex flex-col gap-[12px] px-[6px]">
                <p className="text-[14px] font-[590] leading-[20px] text-white">
                  {t('all_comments')}
                </p>

                <div className="max-h-80 overflow-y-auto overflow-x-hidden">
                  <CommentsList
                    isLoading={isLoading}
                    isError={isError}
                    comments={
                      comments?.map(
                        (comment): CommentBase => ({
                          id: comment.id,
                          message: comment.message,
                          created_at: comment.created_at,
                          seen: comment.seen,
                          author_profile_id: comment.author_profile_id ?? '',
                          author: comment.author,
                        }),
                      ) ?? []
                    }
                    onDelete={deleteComment.mutate}
                    onUpdateSeen={(id) => updateSeen.mutate({ id })}
                    disableSeen={internalSubproject}
                  />
                </div>
              </div>

              {/* Bottom Divider */}
              <div className="h-[0.5px] w-full bg-white/50" />

              {/* Input Section */}
              <div className="px-[6px] pb-[8px]">
                <CommentInput onSend={handleSubmit} />
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
